import { useConfirm } from "@/components/DialogProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { Telemetry } from "@/core/telemetry";
import { useCustomerProjectHours } from "@/features/projects/hooks/useProjectHours";
import { useCustomerProjects } from "@/features/projects/hooks/useProjects";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { getShiftStatus, type ProjectHours } from "@/firestore_types";
import { cn, formatTimeEST } from "@/lib/utils";
import { ProjectHourService } from "@/services/projectHourService";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import type { Timestamp } from "firebase/firestore";
import { Briefcase, CalendarIcon, CheckCircle, Clock, Filter, MapPin, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LocationMapDialog } from "@/components/location-map-dialog";

// Helper to check if location coordinates are valid (not 0,0 or very close to 0)
const isValidLocation = (location?: { lat: number; lng: number }): location is { lat: number; lng: number } => {
  if (!location) return false;
  const threshold = 0.0001;
  return Math.abs(location.lat) > threshold || Math.abs(location.lng) > threshold;
};

export const Route = createFileRoute("/dashboard/customer/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useAuth();
  const { projectHours, loading: hoursLoading } = useCustomerProjectHours(user?.uid || "");
  const { projects, loading: projectsLoading } = useCustomerProjects(user?.uid || "");
  const { workers, loading: workersLoading } = useAllWorkers();
  const { users, loading: usersLoading } = useUsers();
  const confirm = useConfirm();
  
  // Track loading states for debugging
  const loadingStartTime = useRef(Date.now());
  const loadingStateLogged = useRef(false);
  const previousLoadingState = useRef({ hoursLoading, projectsLoading, workersLoading, usersLoading });
  const componentMounted = useRef(false);

  // Log initial mount
  useEffect(() => {
    if (!componentMounted.current) {
      componentMounted.current = true;
      const initialContext = {
        userId: user?.uid || "no-user",
        timestamp: new Date().toISOString(),
      };
      console.log("[Customer Dashboard] Component mounted", initialContext);
      Telemetry.addBreadcrumb({
        category: "customer-dashboard",
        message: `Component mounted with userId: ${initialContext.userId}`,
        level: "info",
      });
    }
  }, [user]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [selectedWorkType, setSelectedWorkType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sort state
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  // Disapproval dialog state
  const [isDisapprovalDialogOpen, setIsDisapprovalDialogOpen] = useState(false);
  const [disapprovalNote, setDisapprovalNote] = useState("");
  const [selectedHourForDisapproval, setSelectedHourForDisapproval] = useState<ProjectHours | null>(
    null,
  );

  // Map dialog state
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<{
    clockInLocation?: { lat: number; lng: number };
    clockOutLocation?: { lat: number; lng: number };
    workerName: string;
    projectName: string;
    focusOn?: 'clockIn' | 'clockOut';
  } | null>(null);

  const openLocationMap = (hour: ProjectHours, focusOn: 'clockIn' | 'clockOut' = 'clockIn') => {
    const clockInLoc = isValidLocation(hour.clockInLocation) ? hour.clockInLocation : undefined;
    const clockOutLoc = isValidLocation(hour.clockedOutLocation) ? hour.clockedOutLocation : undefined;
    setSelectedLocationData({
      clockInLocation: clockInLoc,
      clockOutLocation: clockOutLoc,
      workerName: getWorkerName(hour.workerId),
      projectName: getProjectName(hour.projectId),
      focusOn,
    });
    setIsMapDialogOpen(true);
  };

  // Log loading state changes and detect stuck loading
  useEffect(() => {
    const loadingState = {
      hoursLoading,
      projectsLoading,
      workersLoading,
      usersLoading,
      userId: user?.uid || "no-user",
      projectCount: projects.length,
      hoursCount: projectHours.length,
      workersCount: workers.length,
      usersCount: users.length,
    };

    // Check if any loading state changed
    const prev = previousLoadingState.current;
    if (
      prev.hoursLoading !== hoursLoading ||
      prev.projectsLoading !== projectsLoading ||
      prev.workersLoading !== workersLoading ||
      prev.usersLoading !== usersLoading
    ) {
      console.log("[Customer Dashboard] Loading state changed:", loadingState);
      Telemetry.addBreadcrumb({
        category: "customer-dashboard",
        message: `Loading state: hours=${hoursLoading}, projects=${projectsLoading}, workers=${workersLoading}, users=${usersLoading}`,
        level: "info",
      });
      previousLoadingState.current = { hoursLoading, projectsLoading, workersLoading, usersLoading };
    }

    // Check if we're stuck in loading (more than 10 seconds)
    const elapsedTime = Date.now() - loadingStartTime.current;
    const isLoading = hoursLoading || projectsLoading || workersLoading || usersLoading;
    
    if (isLoading && elapsedTime > 10000 && !loadingStateLogged.current) {
      loadingStateLogged.current = true;
      const errorContext = {
        ...loadingState,
        elapsedTimeMs: elapsedTime,
        timestamp: new Date().toISOString(),
      };
      
      console.error("[Customer Dashboard] Stuck in loading state:", errorContext);
      Telemetry.captureMessage(
        `Customer dashboard stuck in loading for ${Math.round(elapsedTime / 1000)}s`,
        "warning"
      );
      Telemetry.captureError(
        new Error("Customer dashboard infinite loading detected"),
        errorContext
      );
    }

    // Reset logging flag when loading completes
    if (!isLoading && loadingStateLogged.current) {
      loadingStateLogged.current = false;
      console.log("[Customer Dashboard] Loading completed successfully after", elapsedTime, "ms");
      Telemetry.addBreadcrumb({
        category: "customer-dashboard",
        message: `Loading completed in ${Math.round(elapsedTime / 1000)}s`,
        level: "info",
      });
    }
  }, [hoursLoading, projectsLoading, workersLoading, usersLoading, user, projects.length, projectHours.length, workers.length, users.length]);

  // Function to get worker name by ID
  const getWorkerName = (workerId: string) => {
    const worker = workers.find((w) => w.id === workerId);
    return worker?.name || "Unknown Worker";
  };

  // Function to get project name by ID
  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  // Function to get approver name by user ID
  const getApproverName = (userId?: string) => {
    if (!userId) return "N/A";
    const approver = users.find((u) => u.uid === userId);
    return approver?.name || approver?.email || "Unknown User";
  };

  // Function to calculate shift duration
  const calculateShiftDuration = (clockedInAt: Timestamp, clockedOutAt?: Timestamp) => {
    if (!clockedOutAt) return "Incomplete";

    const diffMs = clockedOutAt.toMillis() - clockedInAt.toMillis();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    return `${hours}h ${minutes}m`;
  };

  // Function to calculate total hours for filtered entries
  const calculateTotalHours = () => {
    let totalMinutes = 0;

    filteredHours.forEach((hour) => {
      if (hour.clockedOutAt) {
        const diffMs = hour.clockedOutAt.toMillis() - hour.clockedInAt.toMillis();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        totalMinutes += diffMins;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  // Function to get worker's work type for a specific project
  const getWorkerWorkType = (workerId: string, projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return "Unknown";

    const unitsAttached =
      project.workUnits?.filter((wu) => wu.attachedWorkers?.some((w) => w.workerId === workerId)) ??
      [];

    if (unitsAttached.length > 0) {
      return unitsAttached.map((wu) => wu.name).join(", ");
    } else {
      return "Unassigned";
    }
  };

  // Get all unique work types for filtering
  const getAllWorkTypes = () => {
    const workTypes = new Set<string>();
    projects.forEach((project) => {
      project.workUnits?.forEach((workUnit) => {
        if (workUnit.name) {
          workTypes.add(workUnit.name);
        }
      });
    });
    return Array.from(workTypes).sort();
  };

  // Function to get status badge for a project hour
  const getStatusBadge = (hour: ProjectHours) => {
    const status = getShiftStatus(hour);
    const iconMap = {
      approved: <CheckCircle className="mr-1 h-3 w-3" />,
      pending: <Clock className="mr-1 h-3 w-3" />,
      active: <Clock className="mr-1 h-3 w-3" />,
    };

    return (
      <Badge
        variant={status.badgeVariant}
        className={status.status === "approved" ? "bg-green-100 text-green-800" : ""}
      >
        {iconMap[status.status]}
        {status.text}
      </Badge>
    );
  };

  // Function to handle disapproval
  const handleDisapproval = async () => {
    if (!selectedHourForDisapproval || !disapprovalNote.trim()) {
      return;
    }

    try {
      await ProjectHourService.changeApprovedStatus(
        selectedHourForDisapproval.id,
        false,
        disapprovalNote.trim(),
      );
      // Reset state
      setIsDisapprovalDialogOpen(false);
      setDisapprovalNote("");
    } catch (error) {
      console.error("Error disapproving entry:", error);
      toast.error("Failed to disapprove entry. Please try again.");
    }
    setSelectedHourForDisapproval(null);
  };

  // Function to open disapproval dialog
  const openDisapprovalDialog = (hour: ProjectHours) => {
    setSelectedHourForDisapproval(hour);
    setDisapprovalNote("");
    setIsDisapprovalDialogOpen(true);
  };

  // Get date range based on filter selection
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "today":
        return {
          from: today,
          to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
        };
      case "yesterday": {
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          from: yesterday,
          to: new Date(yesterday.getTime() + 24 * 60 * 60 * 1000 - 1),
        };
      }
      case "last7days":
        return {
          from: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
          to: now,
        };
      case "last30days":
        return {
          from: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
          to: now,
        };
      case "thisMonth":
        return {
          from: new Date(now.getFullYear(), now.getMonth(), 1),
          to: now,
        };
      case "lastMonth": {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { from: lastMonth, to: lastMonthEnd };
      }
      case "custom":
        return customDateRange;
      default:
        return { from: undefined, to: undefined };
    }
  };

  // Filter by selected project, work type, date range, and search query
  const filteredHours = projectHours.filter((hour) => {
    // Project filter
    if (selectedProjectId !== "all" && hour.projectId !== selectedProjectId) {
      return false;
    }

    // Work type filter
    if (selectedWorkType !== "all") {
      const workerWorkType = getWorkerWorkType(hour.workerId, hour.projectId);
      if (workerWorkType !== selectedWorkType) {
        return false;
      }
    }

    // Date filter
    if (dateFilter !== "all") {
      const { from, to } = getDateRange();
      if (from && to) {
        const clockedInDate = hour.clockedInAt.toDate();
        if (clockedInDate < from || clockedInDate > to) {
          return false;
        }
      }
    }

    // Search filter
    if (searchQuery) {
      const workerName = getWorkerName(hour.workerId).toLowerCase();
      const projectName = getProjectName(hour.projectId).toLowerCase();
      const workType = getWorkerWorkType(hour.workerId, hour.projectId).toLowerCase();
      const query = searchQuery.toLowerCase();

      return workerName.includes(query) || projectName.includes(query) || workType.includes(query);
    }

    return true;
  });

  // Sort filtered hours by date (most recent first by default)
  const sortedFilteredHours = filteredHours.sort((a, b) => {
    const aTime = a.clockedInAt.toMillis();
    const bTime = b.clockedInAt.toMillis();
    return sortOrder === "desc" ? bTime - aTime : aTime - bTime;
  });

  // Pagination calculations
  const totalItems = sortedFilteredHours.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHours = sortedFilteredHours.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (hoursLoading || projectsLoading || workersLoading || usersLoading) {
    // Log which specific loading states are active
    const activeLoadingStates = [
      hoursLoading && "projectHours",
      projectsLoading && "projects",
      workersLoading && "workers",
      usersLoading && "users",
    ].filter(Boolean).join(", ");

    console.log(`[Customer Dashboard] Rendering loading spinner. Active: ${activeLoadingStates}`);

    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const allWorkTypes = getAllWorkTypes();

  return (
    <div className="w-full">
      <div className="mx-auto w-full">
        {/* Compact Search and Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                resetPagination();
              }}
              className="h-8 w-48 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <Select
              value={selectedProjectId}
              onValueChange={(value) => {
                setSelectedProjectId(value);
                resetPagination();
              }}
            >
              <SelectTrigger className="h-8 w-40 text-sm">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gray-500" />
            <Select
              value={selectedWorkType}
              onValueChange={(value) => {
                setSelectedWorkType(value);
                resetPagination();
              }}
            >
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="Work Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Work Types</SelectItem>
                {allWorkTypes.map((workType) => (
                  <SelectItem key={workType} value={workType}>
                    {workType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-gray-500" />
            <Select
              value={dateFilter}
              onValueChange={(value) => {
                setDateFilter(value);
                resetPagination();
              }}
            >
              <SelectTrigger className="h-8 w-36 text-sm">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last7days">Last 7 Days</SelectItem>
                <SelectItem value="last30days">Last 30 Days</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dateFilter === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-8 w-60 justify-start text-left text-sm font-normal",
                      !customDateRange.from && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateRange.from ? (
                      customDateRange.to ? (
                        <>
                          {format(customDateRange.from, "MMM d")} -{" "}
                          {format(customDateRange.to, "MMM d, yyyy")}
                        </>
                      ) : (
                        format(customDateRange.from, "MMM d, yyyy")
                      )
                    ) : (
                      "Pick a date range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={customDateRange.from}
                    selected={customDateRange}
                    onSelect={(range) => setCustomDateRange({ from: range?.from, to: range?.to })}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-green-600">{totalItems} entries</span>
            <span className="text-sm font-medium text-blue-600">{calculateTotalHours()} total</span>

            <div className="flex items-center gap-1 rounded-md border">
              <Button
                variant={sortOrder === "desc" ? "default" : "ghost"}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setSortOrder("desc")}
              >
                Latest
              </Button>
              <Button
                variant={sortOrder === "asc" ? "default" : "ghost"}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setSortOrder("asc")}
              >
                Oldest
              </Button>
            </div>

            {totalPages > 1 && (
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </div>

        {/* Project Hours Table */}
        {totalItems > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Project Hours</CardTitle>
              <CardDescription>All work hours for your projects</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Worker</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Work Type</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Approved By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedHours.map((hour, index) => (
                      <TableRow key={hour.id || index}>
                        <TableCell>{getWorkerName(hour.workerId)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {getProjectName(hour.projectId)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {getWorkerWorkType(hour.workerId, hour.projectId)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="flex items-center gap-1">
                              <div className="font-medium">
                                {hour.clockedInAt.toDate().toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  timeZone: "America/New_York",
                                })}
                              </div>
                              {isValidLocation(hour.clockInLocation) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openLocationMap(hour, 'clockIn')}
                                  className="h-6 w-6 p-0 text-blue-600"
                                >
                                  <MapPin className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                            <div className="text-gray-600">
                              {formatTimeEST(hour.clockedInAt)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {hour.clockedOutAt ? (
                            <div className="text-sm">
                              <div className="flex items-center gap-1">
                                <div className="font-medium">
                                  {hour.clockedOutAt.toDate().toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    timeZone: "America/New_York",
                                  })}
                                </div>
                                {isValidLocation(hour.clockedOutLocation) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openLocationMap(hour, 'clockOut')}
                                    className="h-6 w-6 p-0 text-green-600"
                                  >
                                    <MapPin className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                              <div className="text-gray-600">
                                {formatTimeEST(hour.clockedOutAt)}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Incomplete</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-100 text-xs text-blue-800">
                            {calculateShiftDuration(hour.clockedInAt, hour.clockedOutAt)}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(hour)}</TableCell>
                        <TableCell>
                          {hour.approvedStatusChangedBy ? (
                            <div className="space-y-1">
                              <div className="text-sm font-medium">
                                {hour.approved ? "Approved" : "Disapproved"} by{" "}
                                {getApproverName(hour.approvedStatusChangedBy)}
                              </div>
                              {hour.approvedStatusChangedAt && (
                                <div className="text-xs text-gray-500">
                                  {formatTimeEST(hour.approvedStatusChangedAt, true)}
                                </div>
                              )}
                              {!hour.approved && hour.disapprovalNote && (
                                <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 p-2">
                                  <div className="text-xs font-semibold text-orange-800">
                                    Disapproval Reason:
                                  </div>
                                  <div className="mt-1 text-xs text-gray-700">{hour.disapprovalNote}</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {hour.clockedIn && hour.clockedOut && (
                            <Button
                              onClick={async () => {
                                const isApproving = !hour.approved;
                                
                                if (isApproving) {
                                  // Approving - show confirmation
                                  const confirmation = await confirm({
                                    title: "Are you sure you want to approve this time entry?",
                                    description:
                                      "This will approve the time entry and it will be visible to the customer.",
                                    confirmText: "Approve",
                                    cancelText: "Cancel",
                                  });
                                  if (confirmation) {
                                    try {
                                      await ProjectHourService.changeApprovedStatus(hour.id, true);
                                    } catch (error) {
                                      console.error("Error approving entry:", error);
                                      toast.error("Failed to approve entry. Please try again.");
                                    }
                                  }
                                } else {
                                  // Disapproving - open dialog for note
                                  openDisapprovalDialog(hour);
                                }
                              }}
                              size="sm"
                              className={`${
                                hour.approved
                                  ? "bg-orange-500 hover:bg-orange-600"
                                  : "bg-primary hover:bg-primary/90"
                              } transition-colors duration-200`}
                            >
                              {hour.approved ? "Unapprove" : "Approve"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems}{" "}
                    entries
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          className={
                            currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                          }
                        />
                      </PaginationItem>

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }

                        return (
                          <PaginationItem key={pageNumber}>
                            <PaginationLink
                              onClick={() => handlePageChange(pageNumber)}
                              isActive={pageNumber === currentPage}
                              className="cursor-pointer"
                            >
                              {pageNumber}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      {totalPages > 5 && currentPage < totalPages - 2 && (
                        <>
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => handlePageChange(totalPages)}
                              className="cursor-pointer"
                            >
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          className={
                            currentPage === totalPages
                              ? "pointer-events-none opacity-50"
                              : "cursor-pointer"
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="py-12 text-center">
            <CardContent className="pt-6">
              <div className="mb-4 text-6xl text-gray-400">📋</div>
              <h3 className="mb-2 text-xl font-medium text-gray-900">No Hours Found</h3>
              <p className="text-gray-600">
                {searchQuery
                  ? `No hours match your search "${searchQuery}".`
                  : selectedProjectId !== "all" ||
                      selectedWorkType !== "all" ||
                      dateFilter !== "all"
                    ? "No hours found for the selected filters."
                    : "Work hours will appear here."}
              </p>
              {(searchQuery ||
                selectedProjectId !== "all" ||
                selectedWorkType !== "all" ||
                dateFilter !== "all") && (
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedProjectId("all");
                      setSelectedWorkType("all");
                      setDateFilter("all");
                      setCustomDateRange({ from: undefined, to: undefined });
                      resetPagination();
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Disapproval Note Dialog */}
      <Dialog open={isDisapprovalDialogOpen} onOpenChange={setIsDisapprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disapprove Time Entry</DialogTitle>
            <DialogDescription>
              Please explain why you're disapproving this time entry
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Enter your reason for disapproval..."
              value={disapprovalNote}
              onChange={(e) => setDisapprovalNote(e.target.value)}
              rows={4}
              className="resize-none"
            />
            {disapprovalNote.trim() === "" && (
              <p className="text-sm text-red-600">A note is required to disapprove this entry</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDisapprovalDialogOpen(false);
                setDisapprovalNote("");
                setSelectedHourForDisapproval(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisapproval}
              disabled={!disapprovalNote.trim()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Disapprove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Map Dialog */}
      {selectedLocationData && (
        <LocationMapDialog
          isOpen={isMapDialogOpen}
          onClose={() => setIsMapDialogOpen(false)}
          clockInLocation={selectedLocationData.clockInLocation}
          clockOutLocation={selectedLocationData.clockOutLocation}
          workerName={selectedLocationData.workerName}
          projectName={selectedLocationData.projectName}
          focusOn={selectedLocationData.focusOn}
        />
      )}
    </div>
  );
}
